import { useEffect, useState } from 'react';
import { liveChatAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageSquare, Users, Globe, Monitor, Smartphone, Tablet,
  Clock, Star, TrendingUp, BarChart3, PieChart, MapPin,
  RefreshCw, Loader2, ExternalLink, ArrowUp, ArrowDown
} from 'lucide-react';

export const ChatAnalyticsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await liveChatAPI.getAnalytics(period);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (!hasRole(['admin', 'sales_manager', 'team_lead'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <BarChart3 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">Only managers can view chat analytics.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const getDeviceIcon = (type) => {
    if (type === 'mobile') return <Smartphone className="h-4 w-4" />;
    if (type === 'tablet') return <Tablet className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  const totalDevices = analytics ? 
    (analytics.device_breakdown?.desktop || 0) + 
    (analytics.device_breakdown?.mobile || 0) + 
    (analytics.device_breakdown?.tablet || 0) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="chat-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Chat Analytics
          </h1>
          <p className="text-muted-foreground">
            Understand how visitors are engaging with your live chat
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-blue-700">
              <MessageSquare className="h-4 w-4" />
              Total Chats
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-700">{analytics?.total_chats || 0}</p>
            <p className="text-sm text-blue-600 mt-1">
              {analytics?.active_chats || 0} active, {analytics?.waiting_chats || 0} waiting
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-amber-700">
              <Clock className="h-4 w-4" />
              Avg Response Time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-amber-700">
              {analytics?.avg_response_time_seconds 
                ? `${Math.floor(analytics.avg_response_time_seconds / 60)}m ${analytics.avg_response_time_seconds % 60}s`
                : '-'}
            </p>
            <p className="text-sm text-amber-600 mt-1">First response to visitors</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-green-700">
              <Star className="h-4 w-4" />
              Satisfaction
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-700">
              {analytics?.avg_satisfaction_rating?.toFixed(1) || '-'}/5
            </p>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star} 
                  className={`h-4 w-4 ${star <= (analytics?.avg_satisfaction_rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} 
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-red-700">
              <TrendingUp className="h-4 w-4" />
              Missed Chats
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-700">{analytics?.missed_chats || 0}</p>
            <p className="text-sm text-red-600 mt-1">Visitors left without response</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Visitor Locations
            </CardTitle>
            <CardDescription>Where your chat visitors are coming from</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[250px]">
              {analytics?.top_countries?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_countries.map((country, index) => (
                    <div key={country.country} className="flex items-center gap-3">
                      <div className="w-6 text-center font-medium text-muted-foreground">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{country.country}</span>
                          <span className="text-sm text-muted-foreground">{country.count} chats</span>
                        </div>
                        <Progress 
                          value={(country.count / (analytics.total_chats || 1)) * 100} 
                          className="h-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No location data yet</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Traffic Sources */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              Traffic Sources
            </CardTitle>
            <CardDescription>Where chat visitors are coming from</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[250px]">
              {analytics?.top_referrers?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_referrers.map((referrer, index) => (
                    <div key={referrer.referrer} className="flex items-center gap-3">
                      <div className="w-6 text-center font-medium text-muted-foreground">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium truncate">{referrer.referrer || 'Direct'}</span>
                          <span className="text-sm text-muted-foreground">{referrer.count}</span>
                        </div>
                        <Progress 
                          value={(referrer.count / (analytics.total_chats || 1)) * 100} 
                          className="h-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ExternalLink className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No referrer data yet</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Device Breakdown
            </CardTitle>
            <CardDescription>What devices visitors are using</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {['desktop', 'mobile', 'tablet'].map((device) => {
                const count = analytics?.device_breakdown?.[device] || 0;
                const percentage = totalDevices > 0 ? Math.round((count / totalDevices) * 100) : 0;
                return (
                  <div key={device} className="text-center p-4 bg-slate-50 rounded-lg">
                    <div className="flex justify-center mb-2">
                      {getDeviceIcon(device)}
                    </div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground capitalize">{device}</p>
                    <p className="text-sm font-medium text-primary">{percentage}%</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Agent Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Agent Performance
            </CardTitle>
            <CardDescription>How your agents are performing</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {analytics?.agents_performance?.length > 0 ? (
                <div className="space-y-4">
                  {analytics.agents_performance.map((agent) => (
                    <div key={agent.agent_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{agent.name}</p>
                        <p className="text-sm text-muted-foreground">{agent.total_chats} chats handled</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          <span className="font-medium">{agent.avg_rating?.toFixed(1) || '-'}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {agent.avg_response_time ? `${Math.floor(agent.avg_response_time / 60)}m avg` : '-'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No agent data yet</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Satisfaction Breakdown */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Satisfaction Breakdown
            </CardTitle>
            <CardDescription>How visitors rated their chat experience</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4 h-[150px]">
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = analytics?.satisfaction_breakdown?.[rating] || 0;
                const totalRatings = Object.values(analytics?.satisfaction_breakdown || {}).reduce((a, b) => a + b, 0);
                const percentage = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
                return (
                  <div key={rating} className="flex-1 flex flex-col items-center">
                    <div 
                      className={`w-full rounded-t-lg transition-all ${
                        rating >= 4 ? 'bg-green-500' : rating === 3 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ height: `${Math.max(percentage, 5)}%` }}
                    />
                    <div className="mt-2 text-center">
                      <div className="flex items-center justify-center">
                        {Array.from({ length: rating }).map((_, i) => (
                          <Star key={i} className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                      <p className="text-sm font-medium mt-1">{count}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChatAnalyticsPage;
